import { SmartScrollContainer } from '@qr-front/ui/components/SmartScrollContainer'
import { useCallback } from 'react'

import { useTableVirtualizer } from './useTableVirtualizer'

type VirtualizerProps<T> = {
	rows: T[]
	estimateSize: number
	overscan: number
	renderRow: (
		row: T,
		index: number,
		measure: (el: HTMLElement | null) => void,
	) => React.ReactNode
	as?: 'table' | 'ul'
}

export function Virtualizer<T>({
	rows,
	estimateSize,
	overscan,
	renderRow,
	as = 'ul',
}: VirtualizerProps<T>) {
	const { parentRef, virtualizer, virtualItems, topOffset, bottomOffset } =
		useTableVirtualizer(rows.length, estimateSize, overscan)

	const measureElement = useCallback(
		(node: HTMLElement | null) => {
			if (node) virtualizer.measureElement(node)
		},
		[virtualizer],
	)

	if (as === 'table') {
		return (
			<div
				ref={parentRef}
				id="virtualizer-container"
				className="-my-2 min-h-0 flex-1 flex-grow overflow-auto"
			>
				<SmartScrollContainer
					direction="vertical"
					scrollbarPaddingHeight={24}
					className="[&_.simplebar-track.simplebar-horizontal]:!hidden"
				>
					<div className="min-w-0 desktop:min-h-full">
						<table className="w-full min-w-0 table-fixed border-separate border-spacing-y-2 focus-visible:outline-none">
							<colgroup>
								<col className="w-[34%] large-desktop:w-[35%]" />
								<col className="w-[12%]" />
								<col className="w-[25%] large-desktop:w-[23%]" />
								<col className="w-[14%] large-desktop:w-[15%]" />
								<col className="w-[15%]" style={{ minWidth: '180px' }} />
							</colgroup>
							<tbody>
								{topOffset > 0 && (
									<tr aria-hidden="true">
										<td colSpan={5} style={{ height: topOffset }} />
									</tr>
								)}
								{virtualItems.map((virtualRow) =>
									renderRow(
										rows[virtualRow.index]!,
										virtualRow.index,
										measureElement,
									),
								)}
								{bottomOffset > 0 && (
									<tr aria-hidden="true">
										<td colSpan={5} style={{ height: bottomOffset }} />
									</tr>
								)}
							</tbody>
						</table>
					</div>
				</SmartScrollContainer>
			</div>
		)
	}

	return (
		<div
			ref={parentRef}
			id="virtualizer-container"
			className="min-h-0 flex-1 overflow-auto"
		>
			<ul className="flex flex-col gap-2">
				{topOffset > 0 && (
					<li aria-hidden="true" style={{ height: topOffset }} />
				)}
				{virtualItems.map((virtualRow) =>
					renderRow(rows[virtualRow.index]!, virtualRow.index, measureElement),
				)}
				{bottomOffset > 0 && (
					<li aria-hidden="true" style={{ height: bottomOffset }} />
				)}
			</ul>
		</div>
	)
}
